import {
  Directive,
  ElementRef,
  Host,
  ModuleWithProviders,
  NgModule,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { CommonModule } from '@angular/common';

import {
  NbActionsModule,
  NbAutocompleteDirective,
  NbBadgeModule,
  NbButtonModule,
  NbCardModule,
  NbContextMenuModule,
  NbIconModule,
  NbLayoutModule,
  NbMenuModule,
  NbPopoverModule,
  NbSearchModule,
  NbSelectComponent,
  NbSelectModule,
  NbSidebarModule,
  NbThemeModule,
  NbUserModule,
} from '@nebular/theme';

import { NbEvaIconsModule } from '@nebular/eva-icons';
import { NbSecurityModule } from '@nebular/security';

import {
  FooterComponent,
  HeaderComponent,
  SearchInputComponent,
} from './components';

import {
  CapitalizePipe,
  NumberWithCommasPipe,
  PluralPipe,
  RoundPipe,
  TimingPipe,
} from './pipes';

import {
  OneColumnLayoutComponent,
  ThreeColumnsLayoutComponent,
  TwoColumnsLayoutComponent,
} from './layouts';

import { DefaultButtonTypeDirective } from './directives/default-button-type.directive';

import { DEFAULT_THEME } from './styles/theme.default';
import { COSMIC_THEME } from './styles/theme.cosmic';
import { CORPORATE_THEME } from './styles/theme.corporate';
import { DARK_THEME } from './styles/theme.dark';


/* =========================================================
   SHARED HELPERS
   ========================================================= */

function isScrollInsideDropdown(event: Event): boolean {
  const target = event.target;

  if (!(target instanceof Element)) {
    return false;
  }

  return !!(
    target.closest('nb-option-list') ||
    target.closest('.options-list-container') ||
    target.closest('.dropdown-list') ||
    target.closest('.search-dropdown')
  );
}

function hideCustomDropdownsImmediately(): void {
  /*
   * POL / POD / Line on some older screens use a custom
   * <ul class="dropdown-list"> instead of a Nebular overlay.
   *
   * Their component blur handler closes them shortly after blur,
   * but without this immediate hide they can visibly travel with
   * the page for that short delay.
   */
  document
    .querySelectorAll<HTMLElement>('.dropdown-list, .search-dropdown')
    .forEach((element) => {
      element.style.visibility = 'hidden';
      element.style.pointerEvents = 'none';
    });

  /*
   * Remove only our temporary inline override.
   * Normally Angular has already removed the dropdown by then
   * through its existing blur handler.
   */
  window.setTimeout(() => {
    document
      .querySelectorAll<HTMLElement>('.dropdown-list, .search-dropdown')
      .forEach((element) => {
        element.style.removeProperty('visibility');
        element.style.removeProperty('pointer-events');
      });
  }, 250);
}


/* =========================================================
   GLOBAL NEBULAR SELECT SCROLL FIX

   IMPORTANT:
   Listen to wheel/touchmove BEFORE the browser performs the
   actual scroll, then also keep scroll as a fallback for
   scrollbar dragging / keyboard / programmatic scrolling.
   ========================================================= */

@Directive({
  selector: 'nb-select',
})
export class GlobalNbSelectScrollFixDirective
  implements OnInit, OnDestroy {

  private readonly closeSelect = (event: Event): void => {
    if (isScrollInsideDropdown(event)) {
      return;
    }

    this.select.hide();
  };

  private readonly onWheel = (event: Event): void => {
    this.closeSelect(event);
  };

  private readonly onTouchMove = (event: Event): void => {
    this.closeSelect(event);
  };

  private readonly onScroll = (event: Event): void => {
    this.closeSelect(event);
  };

  constructor(
    @Host()
    private readonly select: NbSelectComponent,
  ) {}

  ngOnInit(): void {
    document.addEventListener(
      'wheel',
      this.onWheel,
      true,
    );

    document.addEventListener(
      'touchmove',
      this.onTouchMove,
      true,
    );

    document.addEventListener(
      'scroll',
      this.onScroll,
      true,
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener(
      'wheel',
      this.onWheel,
      true,
    );

    document.removeEventListener(
      'touchmove',
      this.onTouchMove,
      true,
    );

    document.removeEventListener(
      'scroll',
      this.onScroll,
      true,
    );
  }
}


/* =========================================================
   GLOBAL NEBULAR AUTOCOMPLETE SCROLL FIX

   Do NOT rely on input.blur() alone.

   Nebular's autocomplete blur handler does not necessarily
   detach the overlay. We call NbAutocompleteDirective.hide()
   directly, so the overlay disappears immediately before the
   page moves.
   ========================================================= */

@Directive({
  selector: 'input[nbAutocomplete]',
})
export class GlobalNbAutocompleteScrollFixDirective
  implements OnInit, OnDestroy {

  private readonly closeAutocomplete = (
    event: Event,
  ): void => {
    if (isScrollInsideDropdown(event)) {
      return;
    }

    this.autocomplete.hide();
  };

  private readonly onWheel = (event: Event): void => {
    this.closeAutocomplete(event);
  };

  private readonly onTouchMove = (event: Event): void => {
    this.closeAutocomplete(event);
  };

  private readonly onScroll = (event: Event): void => {
    this.closeAutocomplete(event);
  };

  constructor(
    @Host()
    private readonly autocomplete:
      NbAutocompleteDirective<any>,
  ) {}

  ngOnInit(): void {
    document.addEventListener(
      'wheel',
      this.onWheel,
      true,
    );

    document.addEventListener(
      'touchmove',
      this.onTouchMove,
      true,
    );

    document.addEventListener(
      'scroll',
      this.onScroll,
      true,
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener(
      'wheel',
      this.onWheel,
      true,
    );

    document.removeEventListener(
      'touchmove',
      this.onTouchMove,
      true,
    );

    document.removeEventListener(
      'scroll',
      this.onScroll,
      true,
    );
  }
}


/* =========================================================
   GLOBAL CUSTOM TYPEAHEAD SCROLL FIX

   Used for older custom dropdowns such as POL / POD / Line
   that are rendered with:
       <ul class="dropdown-list">

   No component HTML/TS logic is changed.

   On page scroll:
   1. hide the custom dropdown immediately
   2. blur the active nbInput
   3. existing component (blur) logic closes it normally
   ========================================================= */

@Directive({
  selector: 'input[nbInput], textarea[nbInput]',
})
export class GlobalNbInputScrollFixDirective
  implements OnInit, OnDestroy {

  private readonly closeCustomDropdown = (
    event: Event,
  ): void => {
    if (isScrollInsideDropdown(event)) {
      return;
    }

    const input =
      this.elementRef.nativeElement;

    if (document.activeElement !== input) {
      return;
    }

    /*
     * Hide immediately so the list cannot visibly move with
     * the scrolling page while the component's blur timeout
     * is still running.
     */
    hideCustomDropdownsImmediately();

    /*
     * Existing POL / POD / Line inputs already have blur
     * handlers, so this preserves their current functionality.
     */
    input.blur();
  };

  private readonly onWheel = (event: Event): void => {
    this.closeCustomDropdown(event);
  };

  private readonly onTouchMove = (event: Event): void => {
    this.closeCustomDropdown(event);
  };

  private readonly onScroll = (event: Event): void => {
    this.closeCustomDropdown(event);
  };

  constructor(
    private readonly elementRef:
      ElementRef<HTMLInputElement | HTMLTextAreaElement>,
  ) {}

  ngOnInit(): void {
    document.addEventListener(
      'wheel',
      this.onWheel,
      true,
    );

    document.addEventListener(
      'touchmove',
      this.onTouchMove,
      true,
    );

    document.addEventListener(
      'scroll',
      this.onScroll,
      true,
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener(
      'wheel',
      this.onWheel,
      true,
    );

    document.removeEventListener(
      'touchmove',
      this.onTouchMove,
      true,
    );

    document.removeEventListener(
      'scroll',
      this.onScroll,
      true,
    );
  }
}


const NB_MODULES = [
  NbLayoutModule,
  NbMenuModule,
  NbUserModule,
  NbActionsModule,
  NbSearchModule,
  NbSidebarModule,
  NbContextMenuModule,
  NbSecurityModule,
  NbButtonModule,
  NbSelectModule,
  NbIconModule,
  NbEvaIconsModule,
  NbPopoverModule,
  NbBadgeModule,
  NbCardModule,
];

const COMPONENTS = [
  HeaderComponent,
  FooterComponent,
  SearchInputComponent,
  OneColumnLayoutComponent,
  ThreeColumnsLayoutComponent,
  TwoColumnsLayoutComponent,
];

const PIPES = [
  CapitalizePipe,
  PluralPipe,
  RoundPipe,
  TimingPipe,
  NumberWithCommasPipe,
];

const Themes = NbThemeModule.forRoot(
  {
    name: 'default',
  },
  [DEFAULT_THEME]
);

@NgModule({
  imports: [
    CommonModule,
    ...NB_MODULES,
  ],

  declarations: [
    ...COMPONENTS,
    ...PIPES,
    DefaultButtonTypeDirective,

    // Scroll / floating dropdown fixes only
    GlobalNbSelectScrollFixDirective,
    GlobalNbAutocompleteScrollFixDirective,
    GlobalNbInputScrollFixDirective,
  ],

  exports: [
    CommonModule,
    ...NB_MODULES,
    ...PIPES,
    ...COMPONENTS,
    DefaultButtonTypeDirective,

    // Available everywhere ThemeModule is imported
    GlobalNbSelectScrollFixDirective,
    GlobalNbAutocompleteScrollFixDirective,
    GlobalNbInputScrollFixDirective,
  ],
})
export class ThemeModule {

  static forRoot(): ModuleWithProviders<ThemeModule> {
    return {
      ngModule: ThemeModule,
      providers: [
        ...(Themes.providers ?? []),

        {
          provide: 'DEFAULT_THEME',
          useValue: DEFAULT_THEME,
        },
        {
          provide: 'COSMIC_THEME',
          useValue: COSMIC_THEME,
        },
        {
          provide: 'CORPORATE_THEME',
          useValue: CORPORATE_THEME,
        },
        {
          provide: 'DARK_THEME',
          useValue: DARK_THEME,
        },
      ],
    };
  }
}
